'use client';

import NextImage from 'next/image';

import { Heading } from '../Heading/Heading';
import { Text } from '../Text/Text';
import { twMerge } from 'tailwind-merge';
import { Container } from '../Container/Container';
import catchImageBlurred1 from './assets/CatchImageBlurred1.png';
import catchImageBlurred2 from './assets/CatchImageBlurred2.png';
import catchImageBlurred3 from './assets/CatchImageBlurred3.png';
import avatarBlurred1 from './assets/AvatarBlurred1.png';
import avatarBlurred2 from './assets/AvatarBlurred2.png';
import avatarBlurred3 from './assets/AvatarBlurred3.png';
import firstPlaceImage from './assets/firstPlace.svg';
import secondPlaceImage from './assets/secondPlace.svg';
import thirdPlaceImage from './assets/thirdPlace.svg';
import star from './assets/star.svg';
import arrow from './assets/arrow-black.svg';
import { AppStoreButtons } from '../Clickables/Buttons/AppStoreButtons/AppStoreButtons';
import {
  useIsDesktopScreenWidthOrWider,
  useIsMobileScreenWidthOrThinner,
} from '../../helpers/hooks/useScreenWidth';
import { QRCode } from '../QRCode/QRCode';

interface IBitetimeDownloadSectionProps {
  className?: string;
  id: string;
  text?: string;
  title: string;
  sourceId?: string;
}

export const BiggestCatchDownloadSection = ({
  className,
  id = 'biggest-catch-download-section',
  text,
  title = 'When is the best time to fish?',
  sourceId,
}: IBitetimeDownloadSectionProps) => {
  const isDesktopScreenWidthOrWider = useIsDesktopScreenWidthOrWider();
  const isMobileScreenWidthOrThinner = useIsMobileScreenWidthOrThinner();

  return (
    <Container id={id} className={twMerge('w-full', className)}>
      <div className="sm:bg-lake-fog flex flex-col items-center justify-between gap-6 sm:flex-row sm:items-start sm:rounded-xl sm:p-6 md:mx-auto md:max-w-5xl md:p-10">
        {isMobileScreenWidthOrThinner && (
          <Heading as="h2" variant="sm" className="mb-3 w-full sm:mt-auto">
            {title}
          </Heading>
        )}
        <div className="flex w-full max-w-96 flex-row flex-nowrap items-center justify-center gap-2 sm:gap-4 md:max-w-[464px]">
          <div className="relative">
            <div className="flex flex-col items-center justify-center gap-1">
              <div className="relative mb-10">
                <NextImage
                  src={catchImageBlurred1}
                  alt="Download the Bitetime app"
                  className="h-24 w-24 rounded-md md:h-36 md:w-36"
                  unoptimized
                />
                <NextImage
                  src={avatarBlurred1}
                  alt="Download the Bitetime app"
                  className="bg-sea-foam border-sea-foam absolute bottom-0 left-1/2 h-14 w-14 -translate-x-1/2 translate-y-2/3 transform rounded-full border-2"
                  unoptimized
                />
                <div className="bg-sea-foam border-sea-foam absolute top-full bottom-0 left-0 flex h-10 w-10 translate-x-2 items-center justify-center rounded-full border-2 p-1 md:translate-x-4">
                  <NextImage
                    src={firstPlaceImage}
                    alt="Download the Bitetime app"
                    className="size-full object-contain p-px"
                    unoptimized
                  />
                </div>
                <div className="absolute top-full bottom-0 left-0 flex h-8 w-8 translate-x-4 -translate-y-2 items-center justify-center md:translate-x-6 md:-translate-y-2">
                  <NextImage
                    src={star}
                    alt="Download the Bitetime app"
                    className="size-full object-contain"
                    unoptimized
                  />
                </div>
              </div>
              <div className="bg-twilight h-4 w-full rounded-sm"></div>
              <div className="bg-twilight h-4 w-1/2 rounded-sm"></div>
            </div>
          </div>
          <div className="relative">
            <div className="flex flex-col items-center justify-center gap-1">
              <div className="relative mb-10">
                <NextImage
                  src={catchImageBlurred2}
                  alt="Download the Bitetime app"
                  className="h-24 w-24 rounded-md md:h-36 md:w-36"
                  unoptimized
                />
                <NextImage
                  src={avatarBlurred2}
                  alt="Download the Bitetime app"
                  className="bg-sea-foam border-sea-foam absolute bottom-0 left-1/2 h-14 w-14 -translate-x-1/2 translate-y-2/3 transform rounded-full border-2"
                  unoptimized
                />
                <div className="bg-sea-foam border-sea-foam absolute top-full bottom-0 left-0 flex h-10 w-10 translate-x-2 items-center justify-center rounded-full border-2 p-1 md:translate-x-4">
                  <NextImage
                    src={secondPlaceImage}
                    alt="Download the Bitetime app"
                    className="size-full object-contain p-px"
                    unoptimized
                  />
                </div>
              </div>
              <div className="bg-twilight h-4 w-full rounded-sm"></div>
              <div className="bg-twilight h-4 w-1/2 rounded-sm"></div>
            </div>
          </div>
          <div className="relative">
            <div className="flex flex-col items-center justify-center gap-1">
              <div className="relative mb-10">
                <NextImage
                  src={catchImageBlurred3}
                  alt="Download the Bitetime app"
                  className="h-24 w-24 rounded-md md:h-36 md:w-36"
                  unoptimized
                />
                <NextImage
                  src={avatarBlurred3}
                  alt="Download the Bitetime app"
                  className="bg-sea-foam border-sea-foam absolute bottom-0 left-1/2 h-14 w-14 -translate-x-1/2 translate-y-2/3 transform rounded-full border-2"
                  unoptimized
                />
                <div className="bg-sea-foam border-sea-foam absolute top-full bottom-0 left-0 flex h-10 w-10 translate-x-2 items-center justify-center rounded-full border-2 p-1 md:translate-x-4">
                  <NextImage
                    src={thirdPlaceImage}
                    alt="Download the Bitetime app"
                    className="size-full object-contain p-px"
                    unoptimized
                  />
                </div>
              </div>
              <div className="bg-twilight h-4 w-full rounded-sm"></div>
              <div className="bg-twilight h-4 w-1/2 rounded-sm"></div>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center gap-6 sm:gap-3">
          {!isMobileScreenWidthOrThinner && (
            <Heading as="h2" variant="sm" className="mb-0 w-full sm:mt-auto">
              {title}
            </Heading>
          )}
          {text && (
            <Heading as="div" variant="2xs" className="mb-0 text-center font-semibold sm:text-left">
              {text}
            </Heading>
          )}

          <div className="flex w-full flex-col items-center sm:mt-7 sm:items-end md:mt-0">
            {isDesktopScreenWidthOrWider ? (
              <div className="hidden w-full justify-end pt-8 pb-6 sm:flex md:items-center md:justify-end md:py-0">
                <div className="relative pb-2">
                  <Text className={`m-0 -rotate-12 whitespace-pre-line`} variant="sm">
                    {`Scan the QR code 
                                to download the app!`}
                  </Text>
                  <NextImage
                    alt="Arrow pointing to QR code"
                    src={arrow}
                    className="absolute right-0 bottom-0 translate-y-full"
                    unoptimized
                  />
                </div>
                <QRCode
                  className="bg-sea-foam rounded-md p-2 md:ml-4"
                  source="BiggestCatchDownloadSection"
                />
              </div>
            ) : (
              <AppStoreButtons
                orientation="vertical"
                source={'BiggestCatchDownloadSection'}
                sourceId={sourceId}
              />
            )}
          </div>
        </div>
      </div>
    </Container>
  );
};
